
import * as React from "react"
import { cn } from "@/lib/utils"
import { useIsMobile } from "@/hooks/use-mobile"

const Table = React.forwardRef<
  HTMLTableElement,
  React.HTMLAttributes<HTMLTableElement> & {
    onHorizontalScrollChange?: (hasScroll: boolean) => void;
  }
>(({ className, onHorizontalScrollChange, ...props }, ref) => {
  const isMobile = useIsMobile();
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [hasHorizontalScroll, setHasHorizontalScroll] = React.useState(false);
  
  // Check for horizontal scroll
  React.useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    
    const checkHorizontalScroll = () => {
      const hasScroll = container.scrollWidth > container.clientWidth;
      setHasHorizontalScroll(hasScroll);
      onHorizontalScrollChange?.(hasScroll);
    };
    
    // Initial check
    checkHorizontalScroll();
    
    // Check on resize
    const resizeObserver = new ResizeObserver(checkHorizontalScroll);
    resizeObserver.observe(container);
    
    // Also check when content changes
    const mutationObserver = new MutationObserver(checkHorizontalScroll);
    mutationObserver.observe(container, { childList: true, subtree: true });
    
    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [onHorizontalScrollChange]);
  
  return (
    <div 
      ref={containerRef}
      className={cn(
        "w-full relative table-container",
        isMobile ? "overflow-auto -webkit-overflow-scrolling-touch" : "overflow-auto",
        hasHorizontalScroll && "has-horizontal-scroll"
      )} 
      style={{ height: 'calc(100vh - 267px)' }}
    >
      <table
        ref={ref}
        className={cn("w-full caption-bottom text-sm", className)}
        {...props}
      />
    </div>
  )
})
Table.displayName = "Table"

const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead 
    ref={ref} 
    className={cn("[&_tr]:border-b sticky top-0 bg-background z-30", className)} 
    {...props} 
  />
))
TableHeader.displayName = "TableHeader"

const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody
    ref={ref}
    className={cn("[&_tr:last-child]:border-0", className)}
    {...props}
  />
))
TableBody.displayName = "TableBody"

const TableFooter = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tfoot
    ref={ref}
    className={cn(
      "border-t bg-muted/50 font-medium [&>tr]:last:border-b-0",
      className
    )}
    {...props}
  />
))
TableFooter.displayName = "TableFooter"

const TableRow = React.forwardRef<
  HTMLTableRowElement,
  React.HTMLAttributes<HTMLTableRowElement>
>(({ className, ...props }, ref) => (
  <tr
    ref={ref}
    className={cn(
      "border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted",
      className
    )}
    {...props}
  />
))
TableRow.displayName = "TableRow"

const TableHead = React.forwardRef<
  HTMLTableCellElement,
  React.ThHTMLAttributes<HTMLTableCellElement> & { 
    draggable?: boolean; 
    sticky?: boolean; 
    stickyIndex?: number;
    isFirstColumn?: boolean;
    columnIndex?: number;
    symbolColumnIndex?: number;
    hasHorizontalScroll?: boolean;
  }
>(({ className, draggable, sticky, stickyIndex = 0, isFirstColumn, columnIndex, symbolColumnIndex, hasHorizontalScroll, ...props }, ref) => {
  const isStickySymbol = symbolColumnIndex !== undefined && columnIndex === symbolColumnIndex && hasHorizontalScroll;
  
  return (
    <th
      ref={ref}
      className={cn(
        "h-12 px-4 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0 bg-background",
        draggable && "cursor-move",
        sticky && "sticky z-20",
        sticky && `left-[${stickyIndex * 80}px]`,
        isStickySymbol && "sticky-column",
        isFirstColumn && "sticky-first-column-header",
        className
      )}
      style={isStickySymbol ? { left: 0 } : undefined}
      draggable={draggable}
      {...props}
    />
  );
})
TableHead.displayName = "TableHead"

const TableCell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement> & { 
    fixedWidth?: boolean; 
    sticky?: boolean; 
    stickyIndex?: number;
    isFirstColumn?: boolean;
    columnIndex?: number;
    symbolColumnIndex?: number;
    hasHorizontalScroll?: boolean;
  }
>(({ className, fixedWidth, sticky, stickyIndex = 0, isFirstColumn, columnIndex, symbolColumnIndex, hasHorizontalScroll, ...props }, ref) => {
  const isStickySymbol = symbolColumnIndex !== undefined && columnIndex === symbolColumnIndex && hasHorizontalScroll;
  
  return (
    <td
      ref={ref}
      className={cn(
        "p-4 align-middle [&:has([role=checkbox])]:pr-0", 
        fixedWidth && "whitespace-nowrap",
        sticky && "sticky z-10 bg-background",
        sticky && `left-[${stickyIndex * 80}px]`,
        isStickySymbol && "sticky-column",
        isFirstColumn && "sticky-first-column-cell",
        className
      )}
      style={isStickySymbol ? { left: 0 } : undefined}
      {...props}
    />
  );
})
TableCell.displayName = "TableCell"

const TableCaption = React.forwardRef<
  HTMLTableCaptionElement,
  React.HTMLAttributes<HTMLTableCaptionElement>
>(({ className, ...props }, ref) => (
  <caption
    ref={ref}
    className={cn("mt-4 text-sm text-muted-foreground", className)}
    {...props}
  />
))
TableCaption.displayName = "TableCaption"

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
}
